'use strict'
import { useCallback } from 'react'
import { hasErrorClass } from '../extra/common'
import { useEmployeeCountDispatch, useEmployeeCountSelector } from '../extra/store'
import { ShowErrors } from './ShowErrors'
import { formatNumberValue } from '../../../../common/utils/NumberUtils'
import { shallowEqual } from 'react-redux'

/*==================================
 カスタムフック
 ==================================*/
/**
 * 年間の所定労働時間設定に関するロジックを管理するカスタムフック。 入力値の取得、計算、イベントハンドリングを行います。
 *
 * @returns {{
 *   prescribedWorkingHoursYear: object
 *   fiscalYearErrors: object
 *   handleChangeWeeksPerYear: (e: React.ChangeEvent<HTMLInputElement>) => void
 *   handleBlurWeeksPerYear: (e: React.FocusEvent<HTMLInputElement>) => void
 *   handleChangePrescribedWorkingHoursPerWeek: (e: React.ChangeEvent<HTMLInputElement>) => void
 *   handleBlurPrescribedWorkingHoursPerWeek: (e: React.FocusEvent<HTMLInputElement>) => void
 * }}
 */
const usePrescribedWorkingHoursYearLogic = () => {
  const dispatch = useEmployeeCountDispatch()
  const { prescribedWorkingHoursYear, fiscalYearErrors } = useEmployeeCountSelector((state) => {
    const selectedYear = state.employeeCountInfo.selectedFiscalYear
    const fiscalYearSettings = state.employeeCountInfo.fiscalYearSettings[selectedYear]

    return {
      // 配列の0番目の要素にアクセスし、オブジェクトを取得
      prescribedWorkingHoursYear: fiscalYearSettings?.prescribedWorkingHoursYear?.[0] ?? {},
      fiscalYearErrors: fiscalYearSettings?.errors?.client ?? {}
    }
  }, shallowEqual)

  /**
   * 年間週数変更処理（変更アクションをdispatch）=> onChange
   *
   * @returns {(e: React.ChangeEvent<HTMLInputElement>) => void} - イベント（年間週数（入力値））
   */
  const handleChangeWeeksPerYear = useCallback(
    (e) => {
      dispatch('CHANGE_WEEKS_PER_YEAR', { weeksPerYear: e.target.value })
    },
    [dispatch]
  )

  /**
   * 年間週数変更処理（変更アクションをdispatch）=> onBlur
   *
   * @returns {(e: React.FocusEvent<HTMLInputElement>) => void} - イベント（年間週数（入力値））
   */
  const handleBlurWeeksPerYear = useCallback(
    (e) => {
      // トリム処理
      const trimmedValue = e.target.value.trim()
      const formattedValue = formatNumberValue(trimmedValue, 0, false)
      dispatch('CHANGE_WEEKS_PER_YEAR', { weeksPerYear: formattedValue })
    },
    [dispatch]
  )

  /**
   * 「所定労働時間／週」変更処理（変更アクションをdispatch）=> onChange
   *
   * @returns {(e: React.ChangeEvent<HTMLInputElement>) => void} - イベント（「所定労働時間／週」（入力値））
   */
  const handleChangePrescribedWorkingHoursPerWeek = useCallback(
    (e) => {
      dispatch('CHANGE_PRESCRIBED_HOURS_PER_WEEK', { prescribedWorkingHoursPerWeek: e.target.value })
    },
    [dispatch]
  )

  /**
   * 「所定労働時間／週」変更処理（変更アクションをdispatch）=> onBlur
   *
   * @returns {(e: React.FocusEvent<HTMLInputElement>) => void} - イベント（「所定労働時間／週」（入力値））
   */
  const handleBlurPrescribedWorkingHoursPerWeek = useCallback(
    (e) => {
      // トリム処理
      const trimmedValue = e.target.value.trim()
      const formattedValue = formatNumberValue(trimmedValue, 2, false)
      dispatch('CHANGE_PRESCRIBED_HOURS_PER_WEEK', { prescribedWorkingHoursPerWeek: formattedValue })
    },
    [dispatch]
  )

  return {
    prescribedWorkingHoursYear,
    fiscalYearErrors,
    handleChangeWeeksPerYear,
    handleBlurWeeksPerYear,
    handleChangePrescribedWorkingHoursPerWeek,
    handleBlurPrescribedWorkingHoursPerWeek
  }
}

/*==================================
 コンポーネント
 ==================================*/
/**
 * 年間の所定労働時間設定セクションをレンダリングするコンポーネント
 *
 * @returns {React.ReactElement}
 * @component
 */
const PrescribedWorkingHoursYearSetting = () => {
  const {
    prescribedWorkingHoursYear,
    fiscalYearErrors,
    handleChangeWeeksPerYear,
    handleBlurWeeksPerYear,
    handleChangePrescribedWorkingHoursPerWeek,
    handleBlurPrescribedWorkingHoursPerWeek
  } = usePrescribedWorkingHoursYearLogic()

  const {
    weeksPerYear = '',
    prescribedWorkingHoursPerWeek = '',
    prescribedWorkingHoursPerYear = ''
  } = prescribedWorkingHoursYear

  return (
    <>
      <div className="text-body-2-bold grow mt-6 mb-3">■所定労働時間（年）の設定</div>
      <div className="mb-6">
        1年間の週数と1週間当たりの所定労働時間を設定してください。
        <br />
        以下のダッシュボードに適用されます。
        <br />
        1.1.2 通期実労働基準
        <br />
        10.6.1{' '}
        <a className="text-blue-500" href="/master/dashboard/securitiesReportInfo" target="_blank">
          有価証券報告書レポート設定
        </a>
        の「臨時従業員の人数計算方式」で、計算対象が通期実労働基準、計算方法がFTE（実労働時間換算）となっている場合
      </div>
      <div className="">
        <div className="form-control grid grid-cols-[repeat(8,min-content)] grid-rows-[auto_auto] items-center gap-x-4 gap-y-1">
          {/* 年間週数 */}
          <div htmlFor="weeks-per-year" className="text-left text-sm font-medium row-span-1 col-span-1">
            年間週数
          </div>
          <div></div> {/* 空セル */}
          <div></div> {/* 空セル */}
          <label
            htmlFor="prescribed-working-hours-per-week"
            className="text-left text-sm font-medium row-span-1 col-span-1"
          >
            所定労働時間／週
          </label>
          <div className="w-[50px]"></div> {/* 空セル */}
          <div></div> {/* 空セル */}
          <label className="text-left text-sm font-medium row-span-1 col-span-1 w-[110px] whitespace-nowrap">
            所定労働時間／年
          </label>
          {/* 入力欄・演算子 */}
          <input
            id="weeks-per-year"
            name="weeksPerYear"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={weeksPerYear}
            onChange={handleChangeWeeksPerYear}
            onBlur={handleBlurWeeksPerYear}
            className={`text-input w-[200px] row-start-2 col-start-1 text-[14px] ${hasErrorClass('weeksPerYear', fiscalYearErrors) ? 'is-error' : ''}`}
            placeholder="数値を入力"
          />
          <div className="justify-self-center row-start-2 col-start-2">週</div>
          <div className="justify-self-center row-start-2 col-start-3">×</div>
          <input
            id="prescribed-working-hours-per-week"
            name="prescribedWorkingHoursPerWeek"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={prescribedWorkingHoursPerWeek}
            onChange={handleChangePrescribedWorkingHoursPerWeek}
            onBlur={handleBlurPrescribedWorkingHoursPerWeek}
            className={`text-input w-[200px] row-start-2 col-start-4 text-[14px] ${hasErrorClass('prescribedWorkingHoursPerWeek', fiscalYearErrors) ? 'is-error' : ''}`}
            placeholder="数値を入力"
          />
          <div className="justify-self-center row-start-2 col-start-5"> 時間</div>
          <div className="justify-self-center row-start-2 col-start-6">＝</div>
          <div className="row-start-2 col-start-7 justify-self-end">{prescribedWorkingHoursPerYear}時間</div>
        </div>
      </div>
      <ShowErrors clientErrors={fiscalYearErrors} fieldName="prescribedWorkingHoursYearSetting" />
    </>
  )
}

export default PrescribedWorkingHoursYearSetting
